import React, { useCallback } from 'react';

import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import Link from '@atlaskit/link';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import Form from '@atlaskit/form/form';
import { ErrorMessage, HelperMessage } from '@atlaskit/form/messages';
import Field from '@atlaskit/form/field';
import { FormHeader } from '@atlaskit/form/form-header';
import { FormSection } from '@atlaskit/form/form-section';
import { RequiredAsterisk } from '@atlaskit/form/required-asterisk';

import Textfield from '@atlaskit/textfield';
import { RadioGroup } from '@atlaskit/radio';

import type { OptionsPropType } from '@atlaskit/radio/types';
import { cssMap } from '@atlaskit/css';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { fetchApplinkManifestForUrl, fetchThisManifest } from './fetchApplinkManifestForUrl.tsx';
import type { CreateFlowVariant, StageProps } from './StageProps.types.tsx';
import { APPLINKS_IS_OAUTH2_SUPPORTED_PROVIDER_CHECK } from './applinksListPageUrl.constants.tsx';
import { SubpageTemplate } from './SubpageTemplate.tsx';
import { formatBackendError } from './formatBackendError.tsx';

const styles = cssMap({
	fullContainer: {
		height: '100%',
	},
});

const unknownErrorTextMessage = defineMessage({
	defaultMessage: 'We encountered an unknown error.',
	id: 'applinks.administration.ui.unknown-error',
	description:
		'A generic error message for when something goes wrong, but without any further detail.',
});

const createLinkTypeOAuth2NonfinalMessage = defineMessage({
	defaultMessage: 'Atlassian product via OAuth 2.0',
	id: 'applinks.administration.ui.creation-flow-dialog.create-link-type-oauth2-nonfinal',
	description:
		'Indicates the link being created connects to an Atlassian product using OAuth 2.0 authentication.',
});

const createLinkTypeOAuth1NonfinalMessage = defineMessage({
	defaultMessage: 'Direct application link via OAuth 1.0',
	id: 'applinks.administration.ui.creation-flow-dialog.create-link-type-oauth1-nonfinal',
	description:
		'Indicates the link being created connects directly to the target product using OAuth 1.0 authentication.',
});

const createLinkTypeTunnelNonfinalMessage = defineMessage({
	defaultMessage: 'Existing application tunnel',
	id: 'applinks.administration.ui.creation-flow-dialog.create-link-type-tunnel-nonfinal',
	description: 'Indicates the link being created should use an existing "application tunnel".',
});

const requiredFieldsTextMessage = defineMessage({
	defaultMessage: 'Required fields are marked with an asterisk',
	id: 'applinks.administration.ui.creation-flow-dialog.required-fields-text',
	description: 'Text indicating that required fields in a form are marked with an asterisk.',
});

const cancelButtonTextNonfinalMessage = defineMessage({
	defaultMessage: 'Cancel',
	id: 'applinks.administration.ui.creation-flow-dialog.cancel-form-button-text-nonfinal',
	description: 'The button text for cancelling creation of the applink.',
});

const nextButtonTextNonfinalMessage = defineMessage({
	defaultMessage: 'Next',
	id: 'applinks.administration.ui.creation-flow-dialog.next-form-button-text-nonfinal',
	description: 'Next button for continuing creation flow locally for applink, before review stage.',
});

const createApplicationLinkInformationTextNonfinalMessage = defineMessage({
	defaultMessage:
		"An application link requires an existing application tunnel or the application's URL. We use either one to connect the application to Jira.",
	id: 'applinks.administration.ui.creation-flow-dialog.create-application-link-information-text-nonfinal',
	description: 'Info text prior to link that explains what an Applink is.',
});

const createApplicationLinkHowToLinkTextNonfinalMessage = defineMessage({
	defaultMessage: 'How to create an application link',
	id: 'appinks.administration.ui.creation-flow-dialog.create-application-link-how-to-text-nonfinal',
	description: 'Hyperlink text for the how-to guide in creating an applink.',
});

const createHeaderTextNonfinalMessage = defineMessage({
	defaultMessage: 'Create an application link',
	id: 'applinks.administration.ui.creation-flow-dialog.create-header-text-nonfinal',
	description:
		'Header text for modal dialog for applinks creation flow first step, the creation phase.',
});

const createSupportedLinkTypesHelperMessageNonfinalMessage = defineMessage({
	defaultMessage: "Check the application's documentation for supported link types",
	id: 'applinks.administration.ui.creation-flow-dialog.create-supported-link-types-helper-message-nonfinal',
	description:
		'Instructs the user to reference the product documentation to see available link types.',
});

const createUrlEntryHelperMessageNonfinalMessage = defineMessage({
	defaultMessage: 'Enter the URL for the Atlassian product',
	id: 'applinks.administration.ui.creation-flow-dialog.create-url-entry-helper-message-nonfinal',
	description: "Instructs the user to enter the Atlassian product's URL",
});

const createInvalidUrlErrorMessageNonfinalMessage = defineMessage({
	defaultMessage: 'You must enter a valid URL',
	id: 'applinks.administration.ui.creation-flow-dialog.create-invalid-url-error-message-nonfinal',
	description: 'Informs the user they must enter a valid URL, because the current URL is invalid',
});

type CreateSubpageProps = {
	onError: (error: string) => void;
	onSuccess: (newState: StageProps) => void;
	onCancel: () => void;
};

const CreateSubpage = (props: CreateSubpageProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const createLocalisedRadioOptions = useCallback(() => {
		const linkTypeRadioOptions: OptionsPropType = [
			{
				name: 'linkType',
				value: 'OAUTH2.0',
				label: formatMessage(createLinkTypeOAuth2NonfinalMessage),
			},
			{
				name: 'linkType',
				value: 'OAUTH1.0',
				label: formatMessage(createLinkTypeOAuth1NonfinalMessage),
			},
			{
				name: 'linkType',
				value: 'TUNNEL',
				label: formatMessage(createLinkTypeTunnelNonfinalMessage),
			},
		];
		return linkTypeRadioOptions;
	}, [formatMessage]);

	const onFormSubmit = async (data: { linkType: CreateFlowVariant; remoteUrl: string }) => {
		try {
			const remoteManifestPromise = fetchApplinkManifestForUrl(data.remoteUrl);
			const localManifestPromise = fetchThisManifest();
			const remoteManifest = await remoteManifestPromise;
			const localManifest = await localManifestPromise;

			// Check the remote application supports the selected variant type
			if (
				data.linkType === 'OAUTH2.0' &&
				!remoteManifest.inboundAuthenticationTypes.includes(
					APPLINKS_IS_OAUTH2_SUPPORTED_PROVIDER_CHECK,
				)
			) {
				// TODO: Better error message - maybe a custom dialogue?
				throw new Error(
					'The remote application does not support OAuth 2.0 authentication. Please select a different link type.',
				);
			}

			props.onSuccess({
				kind: 'review',
				variant: data.linkType,
				localManifest,
				remoteManifest,
				rpcUrl: data.remoteUrl,
			});
		} catch (err) {
			const maybeError = err instanceof Error ? err : undefined;
			if (data.linkType === 'OAUTH1.0') {
				if (fg('applinks-1093-redirect-error-fix')) {
					props.onSuccess({
						kind: 'manual-create',
						variant: 'OAUTH1.0',
						error: formatBackendError(maybeError, formatMessage(unknownErrorTextMessage)),
					});
				} else {
					props.onSuccess({
						kind: 'manual-create',
						variant: 'OAUTH1.0',
						error: maybeError ? maybeError.message : formatMessage(unknownErrorTextMessage),
					});
				}
			} else {
				log.safeErrorWithoutCustomerData(
					'applinks.administration.list.creation-flow-dialog.create.submitCreateForm',
					'Failed to submit create form',
					maybeError,
				);
				if (fg('applinks-1093-redirect-error-fix')) {
					props.onError(formatBackendError(maybeError, formatMessage(unknownErrorTextMessage)));
				} else {
					props.onError(maybeError ? maybeError.message : formatMessage(unknownErrorTextMessage));
				}
			}
		}
	};

	return (
		<Form<{ linkType: CreateFlowVariant; remoteUrl: string }>
			onSubmit={onFormSubmit}
			xcss={styles.fullContainer}
		>
			{({ formProps, submitting }) => (
				<form {...formProps}>
					<SubpageTemplate
						variant={null}
						kind="create"
						buttons={
							<ButtonGroup label="Form submit options">
								<Button appearance="subtle" onClick={props.onCancel}>
									{formatMessage(cancelButtonTextNonfinalMessage)}
								</Button>
								<Button type="submit" appearance="primary" isLoading={submitting}>
									{formatMessage(nextButtonTextNonfinalMessage)}
								</Button>
							</ButtonGroup>
						}
						content={
							<Stack>
								<FormHeader title={formatMessage(createHeaderTextNonfinalMessage)}>
									<Stack space="space.200">
										<Box>
											{`${formatMessage(createApplicationLinkInformationTextNonfinalMessage)} `}
											<Link
												href="https://support.atlassian.com/jira-cloud-administration/docs/use-applinks-to-link-to-atlassian-products/"
												target="_blank"
											>
												{' '}
												{formatMessage(createApplicationLinkHowToLinkTextNonfinalMessage)}
											</Link>
										</Box>
										<Text size="small">
											{formatMessage(requiredFieldsTextMessage)} <RequiredAsterisk />
										</Text>
									</Stack>
								</FormHeader>
								<FormSection title="Application link type">
									<Field label="Link type" name="linkType" defaultValue="OAUTH2.0" isRequired>
										{({ fieldProps }) => (
											<>
												<RadioGroup {...fieldProps} options={createLocalisedRadioOptions()} />
												<HelperMessage>
													{formatMessage(createSupportedLinkTypesHelperMessageNonfinalMessage)}
												</HelperMessage>
											</>
										)}
									</Field>
								</FormSection>
								<FormSection title="External application details">
									<Field name="remoteUrl" label="Application URL" isRequired>
										{({ fieldProps, error }) => (
											<>
												<Textfield
													autoComplete="off"
													placeholder="https://example.atlassian.net"
													{...fieldProps}
												/>
												{!error && (
													<HelperMessage>
														{formatMessage(createUrlEntryHelperMessageNonfinalMessage)}
													</HelperMessage>
												)}
												{/* TODO: Add validation */}
												{error && (
													<ErrorMessage>
														{formatMessage(createInvalidUrlErrorMessageNonfinalMessage)}
													</ErrorMessage>
												)}
											</>
										)}
									</Field>
								</FormSection>
							</Stack>
						}
					/>
				</form>
			)}
		</Form>
	);
};

export default CreateSubpage;
